import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { clsx } from "clsx";
import { Loader2, Plus, Trash2, Printer, Zap, AlertTriangle, Download, Upload } from "lucide-react";
import { garmentHex, productionOf, type ApparelStudy, type StudyTone } from "@erc/shared";
import { getBackendUrl } from "../../lib/http";
import { useStudioModel, customApi, type CustomListingDetail, type SampleRow } from "../../lib/customListing";
import { InspirationPanel } from "./InspirationPanel";
import { IconSetCard } from "./IconSetCard";
import { InkSelect, PhotoInput, inkLayersOf, photoLayersOf, colorSlotsOf } from "./ExtraValueInputs";

// Custom Liste Studyosu 4. adim: ornekler. Sablondan farkli degerler ve tisort
// tonlariyla tasarimlar basilir (yalniz kod, AI yok). Varsayilan satirlar
// ilhamdaki urunlerden gelir; urun rengi <-> deger eslesmesi korunur.

/** Ornek basligi: degerler (fotograf dosya adresi gosterilmez) */
const caption = (v: Record<string, string>) => Object.values(v).filter((x) => x && !/^(https?:|\/api\/files\/)/.test(x)).join(" · ");

const TONE_LABEL: Record<StudyTone, string> = { light: "Açık", mid: "Orta", dark: "Koyu" };

/** Tisort renginin tonu: renk adindan yaklasik hex, goreli parlaklik */
function toneOf(color: string): StudyTone {
  const h = garmentHex(color);
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
  const l = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return l > 0.6 ? "light" : l > 0.32 ? "mid" : "dark";
}

/** Yil tasiyan deger ("CLASS OF 2027", "2026"): ornek satirlarinda ilhamdaki gibi kalir */
const YEAR_RE = /\b(19|20)\d{2}\b/;

/** Ilhamdaki ornek degeri onerilenle degisen yazi katmanlari (yil tasiyanlar korunur) */
const swapKeysOf = (study: ApparelStudy, layers: ApparelStudy["layers"]) =>
  layers.filter((l) => l.suggestedValues?.length && !study.items.some((it) => YEAR_RE.test(it.values[l.key] ?? "")));

/** Menuden secilen tasarim katmani ("Design 1..25") */
const iconLayerOf = (s: ApparelStudy) => s.layers.find((l) => l.source === "buyer_pick" && l.kind === "graphic_slot" && l.options?.length);

function defaultRows(study: ApparelStudy): SampleRow[] {
  const layers = study.layers.filter((l) => l.source === "buyer_text" && (l.kind === "font_text" || l.kind === "glyph_text"));
  // Alicinin yazdigi degerler ilhamdakiyle ayni kalmasin (kopya riski): onerilen gercekci degerler
  // sirayla. Korunan: yil tasiyan (CLASS OF 2027). Tum urunlerde ayni ornek isim (SMITH FAMILY) de degisir.
  const pick = (key: string, i: number, fallback?: string) => {
    const l = layers.find((x) => x.key === key)!;
    if (fallback && !l.isPlaceholder && swapKeysOf(study, layers).every((x) => x.key !== key)) return fallback;
    return l.suggestedValues?.length ? l.suggestedValues[i % l.suggestedValues.length] : fallback ?? "";
  };
  const base = study.items.length ? study.items : [{ productColor: "white", tone: "light" as StudyTone, values: {} as Record<string, string> }, { productColor: "black", tone: "dark" as StudyTone, values: {} }];
  // Kolaj/mockup icin en az 3 ornek: ilhamdaki urunler tekrarlanir (degerler onerilerden farkli gelir)
  const items = Array.from({ length: Math.max(3, base.length) }, (_, i) => base[i % base.length]);
  const inks = inkLayersOf(study.layers);
  const slots = layers.flatMap((l) => colorSlotsOf(study, l));
  const iconL = iconLayerOf(study);
  return items.slice(0, 8).map((it, i) => ({
    id: `r${i + 1}`,
    productColor: it.productColor,
    tone: it.tone,
    values: {
      ...Object.fromEntries(layers.map((l) => [l.key, pick(l.key, i, it.values[l.key])])),
      // Menuden secilen tasarim: ilhamdaki urunun numarasi, yoksa sirayla
      ...(iconL ? { [iconL.key]: iconL.options!.includes(it.values[iconL.key]) ? it.values[iconL.key] : iconL.options![i % iconL.options!.length] } : {}),
      // Murekkep: ilhamdaki urunun secimi (secenek listesinde varsa), yoksa sirayla
      ...Object.fromEntries(inks.filter((l) => l.kind === "fixed_art").map((l) => [l.key, l.options?.includes(it.values[l.key]) ? it.values[l.key] : l.options?.[i % Math.max(1, l.options?.length ?? 1)] ?? ""])),
      // Yazi katmaninin renk yuvalari: ilhamdaki urunun secimleri (colors), yoksa sirayla
      ...Object.fromEntries(slots.map((sl) => {
        const n = Number(sl.key.split("__c")[1]) - 1;
        const opts = sl.layer.options ?? [];
        const cols = (it as { colors?: Record<string, string[]> }).colors ?? {};
        const v = [cols[sl.layer.key]?.[n], ...Object.values(cols).map((a) => a[n])].find((x) => x && opts.includes(x));
        return [sl.key, v && opts.includes(v) ? v : opts[(i * 2 + n) % Math.max(1, opts.length)] ?? ""];
      })),
    },
  }));
}

export function SamplesStep({ rec, onChanged }: { rec: CustomListingDetail; onChanged: () => void }) {
  const study = rec.study as ApparelStudy | null;
  const images = study?.imageUrls?.length ? study.imageUrls : (rec.source.imageUrls ?? []);
  const ready = rec.template.pkg?.status === "ready" && study?.status === "approved";
  const saved = rec.template.samples;
  const fastRunning = saved?.status === "running";
  const [model, setModel] = useStudioModel(rec);
  const [rows, setRows] = useState<SampleRow[]>(() => (saved?.rows?.length ? saved.rows : study?.layers ? defaultRows(study) : []));
  const [err, setErr] = useState<string | null>(null);
  const print = useMutation({
    mutationFn: () => customApi("POST", `/${rec.id}/samples`, { rows }),
    onSuccess: () => { setErr(null); onChanged(); },
    onError: (e) => setErr((e as Error).message),
  });
  // Fotografli sablon: ornek satirlari icin AI ile farkli kisilerin fotograflari
  const photos = rec.template.samplePhotos;
  const genPhotos = useMutation({
    mutationFn: () => customApi("POST", `/${rec.id}/sample-photos`, { count: Math.min(6, rows.length), model }),
    onSuccess: () => { setErr(null); onChanged(); },
    onError: (e) => setErr((e as Error).message),
  });
  // Ornekler: acik/koyu tisort tasarimi isareti, indirme, kullanicinin hazirladigi tasarimi yukleme.
  // Kolaj ve kartela acik tisorte acik, koyu/orta tisorte koyu tasarimi basar.
  const fileRef = useRef<HTMLInputElement>(null);
  const [upTone, setUpTone] = useState<"light" | "dark">("light");
  const sampleOp = useMutation({
    mutationFn: (o: { method: "PATCH" | "DELETE"; id: string; tone?: "light" | "dark" }) => customApi(o.method, `/${rec.id}/samples/${o.id}`, o.tone ? { tone: o.tone } : undefined),
    onSuccess: () => { setErr(null); onChanged(); },
    onError: (e) => setErr((e as Error).message),
  });
  const upload = useMutation({
    mutationFn: async (f: File) => {
      const dataUrl = await new Promise<string>((ok, bad) => { const rd = new FileReader(); rd.onload = () => ok(String(rd.result)); rd.onerror = () => bad(rd.error); rd.readAsDataURL(f); });
      return customApi("POST", `/${rec.id}/samples/upload`, { dataUrl, tone: upTone });
    },
    onSuccess: () => { setErr(null); onChanged(); },
    onError: (e) => setErr((e as Error).message),
  });
  const download = async (url: string, name: string) => {
    try {
      const res = await fetch(url.startsWith("/") ? `${getBackendUrl()}${url}` : url);
      if (!res.ok) throw new Error(`İndirilemedi (${res.status})`);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(await res.blob());
      a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    } catch (e) { setErr((e as Error).message); }
  };
  // 3. adimdaki taban tasarim aynen ornek olur (indirip yuklemeden)
  const baseUrl = rec.template.base?.status === "ready" ? rec.template.base.url : undefined;
  const fromBase = useMutation({
    mutationFn: () => customApi("POST", `/${rec.id}/samples/upload`, { fromBase: true }),
    onSuccess: () => { setErr(null); onChanged(); },
    onError: (e) => setErr((e as Error).message),
  });
  // Hizli liste: sablon kurulmadan her satir AI ile ayri cizilir
  const fast = useMutation({
    mutationFn: () => customApi("POST", `/${rec.id}/samples-fast`, { rows: rows.slice(0, 6), model }),
    onSuccess: () => { setErr(null); onChanged(); },
    onError: (e) => setErr((e as Error).message),
  });

  if (!study || study.status !== "approved") {
    return (
      <>
        <InspirationPanel images={images} url={rec.analysis?.url} title={rec.name} />
        <p className="text-sm text-gray-600">Örnekler onaylı liste çalışmasına dayanır. Önce 2. adımda çalışmayı onayla.</p>
      </>
    );
  }
  const layers = study.layers.filter((l) => l.source === "buyer_text" && (l.kind === "font_text" || l.kind === "glyph_text"));
  // Sabit cizim murekkebi (tek secim) ve yazi katmaninin renk yuvalari (ana/ikinci renk)
  const inkLayers = inkLayersOf(study.layers).filter((l) => l.kind === "fixed_art"), photoLayers = photoLayersOf(study.layers);
  // Ayni soru (Design Color) birden cok yazi katmanini boyar: tek sutun, secim hepsine yazilir
  const allSlots = layers.flatMap((l) => colorSlotsOf(study, l));
  const slotCols = [...new Map(allSlots.map((sl) => [sl.label, sl])).values()].map((sl) => ({ ...sl, keys: allSlots.filter((x) => x.label === sl.label).map((x) => x.key) }));
  const iconL = iconLayerOf(study);
  // Ilhamdaki ornek degerler (SMITH FAMILY, Austin, TX) satirlarda varsayilan olarak degisir; geri alinabilir
  const swapLayers = swapKeysOf(study, layers);
  const origVals = new Set(swapLayers.flatMap((l) => study.items.map((it) => it.values[l.key]).filter(Boolean)));
  const usingOrig = rows.some((r) => swapLayers.some((l) => origVals.has(r.values[l.key])));
  const origText = [...origVals].slice(0, 3).join(", ");
  const iconUrl = (v?: string) => rec.template.iconSet?.icons.find((i) => i.label === v)?.url;
  const setRow = (i: number, patch: Partial<SampleRow>) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <div className="space-y-5">
      <InspirationPanel images={images} url={rec.analysis?.url} title={rec.name} />
      <p className="text-sm text-gray-600">{rec.isGarment !== false ? "Her satır bir tişört: rengi, tonu ve üstüne basılacak değerler." : "Her satır bir ürün: üstüne basılacak değerler."} Varsayılan satırlar ilhamdaki ürünlerden gelir; yer tutucular yerine gerçekçi değerler konmuştur. Değiştirip istediğin kadar satır ekleyebilirsin (en fazla 12).</p>
      {iconL && <IconSetCard rec={rec} layer={iconL} model={model} onChanged={onChanged} />}
      {!ready && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 text-sm text-gray-700">
          <p className="flex items-center gap-1.5 font-medium text-gray-900"><Zap className="h-4 w-4 text-indigo-600" /> Hızlı liste (şablonsuz)</p>
          <p className="mt-1 text-xs text-gray-600">
            Şablon henüz hazır değil. Listeyi hemen hazırlamak için her satır AI ile ayrı bir tasarım olarak çizilir (en fazla 6, satır başına yaklaşık 1 dakika, her biri bir görsel ücreti).
            İsimler ilhamdakinden farklı tutulur; liste ilhamın kopyası olmasın. {productionOf(study).mode === "ai" ? "Bu liste AI yolunda: sipariş gelince alıcının tasarımı da AI ile çizilir." : "Harf seti ve şablon sipariş gelince 3. adımdan tamamlanır; alıcının adı o zaman şablondan basılır."}
          </p>
        </div>
      )}

      {swapLayers.length > 0 && (
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
          {usingOrig
            ? <>İlhamdaki örnek değerler kullanılıyor ({origText}); liste ilhamın kopyası gibi görünebilir.</>
            : <>İlhamdaki örnek değerler ({origText}) önerilen değerlerle değiştirildi; liste ilhamın kopyası olmasın.</>}
          <button onClick={() => setRows((rs) => rs.map((r, i) => ({ ...r, values: { ...r.values, ...Object.fromEntries(swapLayers.map((l) => [l.key,
            usingOrig ? l.suggestedValues![i % l.suggestedValues!.length] : study.items[i % Math.max(1, study.items.length)]?.values[l.key] ?? r.values[l.key]])) } })))}
            className="font-medium text-indigo-600 hover:underline">
            {usingOrig ? "Önerilen değerlere geç" : "Orijinaline dön"}
          </button>
        </p>
      )}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs text-gray-500">
            <tr>
              <th className="px-3 py-2">{rec.isGarment !== false ? "Tişört rengi" : "Ürün rengi"}</th>
              {rec.isGarment !== false && <th className="px-3 py-2">Ton</th>}
              {layers.map((l) => <th key={l.key} className="px-3 py-2">{l.labelTr}</th>)}
              {iconL && <th className="px-3 py-2">{iconL.labelTr}</th>}
              {inkLayers.map((l) => <th key={l.key} className="px-3 py-2">{l.labelTr}</th>)}
              {slotCols.map((sl) => <th key={sl.key} className="px-3 py-2">{sl.label}</th>)}
              {ready && photoLayers.map((l) => <th key={l.key} className="px-3 py-2">{l.labelTr}</th>)}
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className="border-t border-gray-100">
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="h-4 w-4 flex-shrink-0 rounded-full border border-gray-300" style={{ background: garmentHex(r.productColor) }} />
                    <input aria-label={rec.isGarment !== false ? "Tişört rengi" : "Ürün rengi"} value={r.productColor} onChange={(e) => setRow(i, { productColor: e.target.value, tone: toneOf(e.target.value) })}
                      className="w-32 rounded-lg border border-gray-200 px-2 py-1 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                  </div>
                </td>
                {rec.isGarment !== false && (
                  <td className="px-3 py-2">
                    <select aria-label="Ton" value={r.tone} onChange={(e) => setRow(i, { tone: e.target.value as StudyTone })}
                      className="rounded-lg border border-gray-200 px-2 py-1 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200">
                      {(["light", "mid", "dark"] as StudyTone[]).map((t) => <option key={t} value={t}>{TONE_LABEL[t]}</option>)}
                    </select>
                  </td>
                )}
                {layers.map((l) => (
                  <td key={l.key} className="px-3 py-2">
                    <input aria-label={l.labelTr} value={r.values[l.key] ?? ""} maxLength={80}
                      onChange={(e) => setRow(i, { values: { ...r.values, [l.key]: e.target.value } })}
                      className="w-full min-w-[9rem] rounded-lg border border-gray-200 px-2 py-1 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                  </td>
                ))}
                {iconL && (
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      {iconUrl(r.values[iconL.key]) && <img src={iconUrl(r.values[iconL.key])} alt="" className="h-7 w-7 rounded border border-gray-200 bg-white object-contain" />}
                      <select aria-label={iconL.labelTr} value={r.values[iconL.key] ?? ""} onChange={(e) => setRow(i, { values: { ...r.values, [iconL.key]: e.target.value } })}
                        className="rounded-lg border border-gray-200 px-2 py-1 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200">
                        <option value="">Seç</option>
                        {iconL.options!.map((o, k) => <option key={o} value={o}>{o}{rec.template.iconSet?.icons[k]?.subject ? ` · ${rec.template.iconSet.icons[k].subject}` : ""}</option>)}
                      </select>
                    </div>
                  </td>
                )}
                {inkLayers.map((l) => (
                  <td key={l.key} className="px-3 py-2">
                    <InkSelect compact layer={l} value={r.values[l.key] ?? ""} onChange={(v) => setRow(i, { values: { ...r.values, [l.key]: v } })} />
                  </td>
                ))}
                {slotCols.map((sl) => (
                  <td key={sl.key} className="px-3 py-2">
                    <InkSelect compact layer={{ ...sl.layer, labelTr: sl.label }} value={r.values[sl.key] ?? ""} onChange={(v) => setRow(i, { values: { ...r.values, ...Object.fromEntries(sl.keys.map((k) => [k, v])) } })} />
                  </td>
                ))}
                {ready && photoLayers.map((l) => (
                  <td key={l.key} className="px-3 py-2">
                    <PhotoInput recId={rec.id} layer={l} value={r.values[l.key] ?? ""} onChange={(v) => setRow(i, { values: { ...r.values, [l.key]: v } })} />
                  </td>
                ))}
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} title="Satırı sil" aria-label="Satırı sil"
                    className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setRows((rs) => [...rs, { id: `r${Date.now() % 100000}`, productColor: "white", tone: "light", values: Object.fromEntries(layers.map((l) => [l.key, l.suggestedValues?.[rs.length % Math.max(1, l.suggestedValues?.length ?? 1)] ?? ""])) }])}
          disabled={rows.length >= 12}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
          <Plus className="h-3.5 w-3.5" /> Satır ekle
        </button>

        {ready ? (
          <>
            <button onClick={() => print.mutate()} disabled={print.isPending || rows.length === 0}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
              {print.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />} Örnekleri bas ({rows.length})
            </button>
            <span className="text-xs text-gray-500">Şablondan kodla basılır; AI ücreti yok.</span>
          </>
        ) : (
          <>
            <label htmlFor="fast-model" className="sr-only">Görsel modeli</label>
            <select id="fast-model" value={model} onChange={(e) => setModel(e.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200">
              {rec.imageModels.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            <button onClick={() => fast.mutate()} disabled={fast.isPending || fastRunning || rows.length === 0}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
              {fast.isPending || fastRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
              {fastRunning ? "Çiziliyor…" : `AI ile örnekleri çiz (${Math.min(6, rows.length)})`}
            </button>
            {baseUrl && !saved?.renders?.some((r) => r.fromBase) && (
              <button onClick={() => fromBase.mutate()} disabled={fromBase.isPending || fastRunning} title="3. adımdaki taban tasarım, yazılarıyla aynen örnek olur (ücretsiz)"
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                {fromBase.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <img src={baseUrl} alt="" className="h-5 w-5 rounded object-cover" />}
                Taban tasarımı örnek olarak ekle
              </button>
            )}
          </>
        )}
      </div>
      {ready && photoLayers.length > 0 && (
        <div className="space-y-2 rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-600">Her satırda aynı örnek fotoğraf var. Kolaj ve mockuplarda farklı kişiler daha gerçekçi görünür: AI aynı poz ve kadrajla yeni kişiler çizer (fotoğraf başına ~$0.03; gerçek bir kişinin fotoğrafı kullanılmaz).</p>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => genPhotos.mutate()} disabled={genPhotos.isPending || photos?.status === "running" || rows.length === 0}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {genPhotos.isPending || photos?.status === "running" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4 text-indigo-600" />}
              {photos?.status === "running" ? "Fotoğraflar üretiliyor…" : `AI ile örnek fotoğraflar üret (${Math.min(6, rows.length)})`}
            </button>
            {photos?.status !== "running" && (photos?.urls?.length ?? 0) > 0 && (
              <button onClick={() => photoLayers.forEach((l) => setRows((rs) => rs.map((r, i) => (photos!.urls[i] ? { ...r, values: { ...r.values, [l.key]: photos!.urls[i] } } : r))))}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700">
                Fotoğrafları satırlara uygula ({photos!.urls.length})
              </button>
            )}
          </div>
          {photos?.error && photos.status !== "running" && <p className="flex items-center gap-1 text-xs text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> {photos.error}</p>}
        </div>
      )}
      {err && <p className="text-sm text-red-600">{err}</p>}
      {saved?.error && !fastRunning && <p className="flex items-center gap-1 text-sm text-amber-700"><AlertTriangle className="h-4 w-4" /> {saved.error}</p>}
      {saved?.mode === "fast" && saved.renders?.length > 0 && (
        <p className="text-xs text-amber-700">{productionOf(study).mode === "ai"
          ? "Bu örnekler AI ile çizildi (üretim yolu: AI). Sipariş gelince alıcının tasarımı da AI ile çizilir; yazı harf harf kontrol edilip onayınla basılır."
          : "Bu örnekler şablonsuz (hızlı liste) çizildi. Sipariş gelince 3. adımda şablonu kur; alıcının adı şablondan basılır."}</p>
      )}

      {saved?.renders?.length ? (
        <div>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-700">Basılan örnekler</h3>
            {rec.isGarment !== false && (
              <span className="ml-auto inline-flex items-center gap-2">
                <label htmlFor="up-tone" className="sr-only">Yüklenecek tasarımın tonu</label>
                <select id="up-tone" value={upTone} onChange={(e) => setUpTone(e.target.value as "light" | "dark")}
                  className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200">
                  <option value="light">Açık tişört tasarımı</option>
                  <option value="dark">Koyu tişört tasarımı</option>
                </select>
                <button onClick={() => fileRef.current?.click()} disabled={upload.isPending}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                  {upload.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Tasarım yükle
                </button>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" aria-label="Tasarım dosyası yükle"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ""; }} />
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {saved.renders.map((r) => {
              const light = r.tone === "light";
              return (
                <figure key={r.id} className="overflow-hidden rounded-lg border border-gray-200">
                  <img src={r.url} alt={caption(r.values)} loading="lazy" className="aspect-square w-full object-contain p-2" style={{ background: garmentHex(r.productColor) }} />
                  <figcaption className="space-y-1 border-t border-gray-100 px-2 py-1.5 text-xs text-gray-600">
                    <p className="truncate">{r.productColor} · {caption(r.values)}</p>
                    <div className="flex items-center gap-1">
                      {rec.isGarment !== false && (
                        <div className="inline-flex rounded-md border border-gray-200 p-0.5" role="group" aria-label="Tasarımın tişört tonu">
                          {(["light", "dark"] as const).map((t) => (
                            <button key={t} onClick={() => sampleOp.mutate({ method: "PATCH", id: r.id, tone: t })} aria-pressed={light === (t === "light")} disabled={sampleOp.isPending}
                              className={clsx("rounded px-2 py-0.5 text-[11px] font-medium", light === (t === "light") ? "bg-indigo-50 text-indigo-700" : "text-gray-500 hover:bg-gray-50")}>{t === "light" ? "Açık" : "Koyu"}</button>
                          ))}
                        </div>
                      )}
                      <button onClick={() => void download(r.url, `tasarim-${light ? "acik" : "koyu"}-${r.id}.png`)} title="Tasarımı indir" aria-label="Tasarımı indir"
                        className="ml-auto rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"><Download className="h-3.5 w-3.5" /></button>
                      <button onClick={() => { if (confirm("Bu örnek kaldırılsın mı?")) sampleOp.mutate({ method: "DELETE", id: r.id }); }} title="Örneği kaldır" aria-label="Örneği kaldır"
                        className="rounded p-1 text-gray-500 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </figcaption>
                </figure>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-gray-500">Kolaj ve mockuplar 5. adımda bu örneklerden kurulur{rec.isGarment !== false ? "; açık tişörtlere açık, koyu tişörtlere koyu tasarım basılır. Tasarımı indirip düzenleyebilir, açık ya da koyu sürümünü yükleyebilirsin. Değişiklikten sonra 6. adımda Mockupları yeniden bas" : ""}.</p>
        </div>
      ) : null}
    </div>
  );
}
